import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Card from "../Card";
import {
  addDriver,
  getDriver,
  updateDriver,
} from "../../services/OrganisationServices/transportManagemetService";
import {
  callToast,
  showErrorMessages,
} from "../../services/utilityFunctions.js";
import { useGlobalState } from "../../services/hostelConst";

const AddDriverForm = ({ refresh, navigate }) => {
  const { closeModal, masterTableState, updateMasterTable, modalState } =
    useGlobalState();
  const id = modalState.id;

  const [loadingData, setLoadingData] = useState(modalState.isEditing);

  const prepareFormData = (data = null) => {
    return {
      name: data == null ? "" : data.NAME,
      email: data == null ? "" : data.EMAIL,
      phone: data == null ? "" : data.PHONE,
      licenseNumber: data == null ? "" : data.LICENSE_NUMBER,
      licenseType: data == null ? "" : data.LICENSE_TYPE,
      experience: data == null ? "" : data.EXPERIENCE,
      address: data == null ? "" : data.ADDRESS,
      emergencyContact: data == null ? "" : data.EMERGENCY_CONTACT,
    };
  };

  const [formData, setFormData] = useState(prepareFormData());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const getDriverDetailsData = async (id) => {
    setLoadingData(true);
    var res = await getDriver(id);
    if (res.status == "success") {
      setFormData(prepareFormData(res.data));
    } else {
      showErrorMessages({ data: res, navigate });
      closeModal();
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (modalState.isEditing && id) {
      getDriverDetailsData(id);
    }
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Driver name is required";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    if (!formData.licenseNumber.trim()) {
      newErrors.licenseNumber = "License number is required";
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        licenseNumber: formData.licenseNumber,
        licenseType: formData.licenseType,
        experience: formData.experience ? parseInt(formData.experience) : null,
        address: formData.address,
        emergencyContact: formData.emergencyContact,
      };

      let res;
      if (modalState.isEditing) {
        submitData.entityId = id;
        res = await updateDriver(submitData);
      } else {
        res = await addDriver(submitData);
      }

      if (res.status === "success") {
        callToast(
          "success",
          "Success",
          `Driver ${modalState.isEditing ? "updated" : "added"} successfully`
        );
        updateMasterTable(true);
        closeModal();
      } else {
        showErrorMessages({ data: res, navigate });
      }
    } catch (error) {
      console.error("Error submitting driver:", error);
    }
    setLoading(false);
  };

  if (loadingData) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Driver Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  isInvalid={!!errors.name}
                  placeholder="Enter driver name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number *</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  isInvalid={!!errors.phone}
                  placeholder="Enter phone number"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  isInvalid={!!errors.email}
                  placeholder="Enter email address"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>License Number *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.licenseNumber}
                  onChange={(e) =>
                    handleChange("licenseNumber", e.target.value)
                  }
                  isInvalid={!!errors.licenseNumber}
                  placeholder="Enter license number"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.licenseNumber}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>License Type</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.licenseType}
                  onChange={(e) => handleChange("licenseType", e.target.value)}
                  placeholder="Enter license type (e.g., Commercial, Heavy Vehicle)"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Experience (Years)</Form.Label>
                <Form.Control
                  type="number"
                  value={formData.experience}
                  onChange={(e) => handleChange("experience", e.target.value)}
                  placeholder="Enter years of experience"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Emergency Contact</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.emergencyContact}
                  onChange={(e) =>
                    handleChange("emergencyContact", e.target.value)
                  }
                  placeholder="Enter emergency contact number"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="Enter driver's address"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {modalState.isEditing ? "Updating..." : "Adding..."}
                </>
              ) : modalState.isEditing ? (
                "Update Driver"
              ) : (
                "Add Driver"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default AddDriverForm;
